import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Space, Spin, Typography } from 'antd'
import { filter, get, isEmpty, join, map, some } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'
import { useQueryParams } from '../../../core/hooks'

import { PermissionGuard } from '../../../common/components/routes'
import {
  FormButtons,
  HistoryLink,
  ReduxMetadataTree,
} from '../../../common/components/sections'
import {
  StyledPageActions,
  StyledPageHeader,
  StyledPageTitle,
  StyledVerticalSpace,
} from '../../../common/components/styled'
import {
  EVENT_METADATA_TYPES,
  METADATA_TYPES,
  PERMISSIONS,
} from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useDocumentTitle,
  useLatestEventByEntityTypesQuery,
  useUserHasPermissions,
} from '../../../common/hooks'

import { renderIngredientEvent } from '../../helpers'
import {
  useDeleteIngredientMutation,
  useIngredientsQuery,
  useReplaceIngredientMutation,
  useRevertReplaceIngredientMutation,
  useSaveIngredientsMutation,
  useSetIsCurationEnabledIngredientMutation,
} from '../../hooks'
import {
  addIngredientNode,
  expandIngredientMainTreeKeys,
  expandIngredientSupportingTreeKeys,
  moveIngredients,
  renameIngredientNode,
  resetIngredientTree,
  setDataForRenamingIngredient,
} from '../../redux/actions'
import {
  IngredientReplaceConflictingErrors,
  IngredientSearchByKeyword,
} from '../sections'

const IngredientsPage = () => {
  const dispatch = useDispatch()
  useDocumentTitle('Ingredients')
  const { keywordSearchTerm, areDiscardedKeywordsIncluded } = useQueryParams()
  const [isMoveActive, setIsMoveActive] = useState(false)
  const [isReplaceActive, setIsReplaceActive] = useState(false)
  const eventEntityTypes = useMemo(() => [EVENT_METADATA_TYPES.INGREDIENT], [])

  const {
    isLoading: isFetchLatestEventLoading,
    error: fetchLatestEventError,
    data: latestEvent,
    refetch: refetchLastEvent,
  } = useLatestEventByEntityTypesQuery(eventEntityTypes)

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    initialData,
  } = useIngredientsQuery(keywordSearchTerm, areDiscardedKeywordsIncluded)

  const [saveIngredients, { isLoading: isSaveLoading, error: saveError }] =
    useSaveIngredientsMutation(refetchLastEvent)

  const [deleteIngredient] = useDeleteIngredientMutation(refetchLastEvent)

  const [setIsCurationEnabledIngredient] =
    useSetIsCurationEnabledIngredientMutation(refetchLastEvent)

  const [replaceIngredient] = useReplaceIngredientMutation(refetchLastEvent)

  const [revertReplaceIngredient] =
    useRevertReplaceIngredientMutation(refetchLastEvent)

  const {
    isDirty,
    data,
    dataForRenaming,
    mainTreeExpandedKeys,
    supportingTreeExpandedKeys,
  } = useSelector(state => state.ingredients.ingredients)

  const hasFullPermission = useUserHasPermissions([
    PERMISSIONS.METADATA_TREE.SAVE,
  ])

  const hasPermissionToViewDetails = useUserHasPermissions(
    PERMISSIONS.METADATA_TREE.GET,
  )

  const treeData = isDirty ? data : initialData

  const handleAdd = useCallback(
    node => dispatch(addIngredientNode(node)),
    [dispatch],
  )

  const handleDelete = useCallback(
    ({ id }) => deleteIngredient(id),
    [deleteIngredient],
  )

  const handleRevertReplaceIngredient = useCallback(
    ({ replaceToken }) => revertReplaceIngredient(replaceToken),
    [revertReplaceIngredient],
  )

  const handleSetIsCurationEnabled = useCallback(
    ({ id, isCurationEnabled }) =>
      setIsCurationEnabledIngredient(id, isCurationEnabled),
    [setIsCurationEnabledIngredient],
  )

  const handleRename = useCallback(
    ({ path, name }) => dispatch(renameIngredientNode({ path, name })),
    [dispatch],
  )

  const handleSetDataForRenaming = useCallback(
    ({ id }) => dispatch(setDataForRenamingIngredient({ id })),
    [dispatch],
  )

  const handleMove = useCallback(
    (nodesToMove, destinationNodes) => {
      const [destinationNode] = destinationNodes
      const levelToMoveUnder = destinationNode.level - 1

      const parentSelectedNodes = filter(
        nodesToMove,
        ({ parentId }) => !some(nodesToMove, ({ id }) => id === parentId),
      )

      const restrictedToMoveNodes = filter(parentSelectedNodes, node => {
        if (levelToMoveUnder > node.level) {
          return !isEmpty(node.children)
        }

        if (levelToMoveUnder === node.level) {
          return !isEmpty(
            node.level === 1
              ? get(node, 'children[0].children')
              : node.children,
          )
        }

        return false
      })

      if (!isEmpty(restrictedToMoveNodes)) {
        showErrorNotification({
          title: 'Move Error',
          description: (
            <div>
              The following nodes can&apos;t be moved:
              {map(restrictedToMoveNodes, ({ name }) => (
                <Typography.Paragraph style={{ margin: 0 }} key={name}>
                  {name};
                </Typography.Paragraph>
              ))}
            </div>
          ),
        })
      } else {
        dispatch(
          moveIngredients({
            nodesToMove: parentSelectedNodes,
            destinationNode,
          }),
        )
      }
    },
    [dispatch],
  )

  const handleReplace = useCallback(
    async (nodesToReplace, replacementNodes) => {
      const [nodeToReplace] = nodesToReplace

      if (nodeToReplace.isCurationEnabled) {
        return showErrorNotification({
          title: 'Replace Error',
          description:
            "The node can't be replaced as it's still enabled for curation",
        })
      }

      if (nodeToReplace.isWholeFood && !some(replacementNodes, 'isWholeFood')) {
        return showErrorNotification({
          title: 'Replace Error',
          description:
            "Whole food ingredient can't be replaced by non whole food",
        })
      }

      if (
        some(
          replacementNodes,
          replacementNode => !isEmpty(replacementNode.replacedBy),
        )
      ) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't replace as it's already being replaced by ${join(
            map(
              filter(
                replacementNodes,
                replacementNode => !isEmpty(replacementNode.replacedBy),
              ),
              'replacedBy.name',
            ),
            ', ',
          )}`,
        })
      }

      if (!isEmpty(nodeToReplace.replacedBy)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't be replaced as it's already being replaced by ${join(
            map(nodeToReplace.replacedBy, 'name'),
            ',',
          )}`,
        })
      }

      if (!isEmpty(nodeToReplace.replaces)) {
        return showErrorNotification({
          title: 'Replace Error',
          description: `The node can't be replaced as it's replacing other nodes ${join(
            map(nodeToReplace.replaces, 'name'),
            ',',
          )}`,
        })
      }

      await replaceIngredient({
        idToReplace: nodeToReplace.id,
        replacementIds: map(replacementNodes, 'id'),
      })

      return setIsReplaceActive(false)
    },
    [replaceIngredient],
  )

  const handleResetTree = useCallback(
    () => dispatch(resetIngredientTree()),
    [dispatch],
  )

  const handleSaveData = useCallback(
    () => saveIngredients(data),
    [data, saveIngredients],
  )

  const computeNodeHighlight = useCallback(
    node => (node.hasUncategorizedKeywords ? 'warning' : undefined),
    [],
  )

  useEffect(() => {
    if (saveError?.message || fetchError?.message || fetchLatestEventError) {
      showErrorNotification({
        title: 'Ingredient error',
        description:
          saveError?.message || fetchError?.message || fetchLatestEventError,
      })
    }
  }, [fetchError, fetchLatestEventError, saveError])

  const { conflicts } = saveError || {}

  return (isFetchLoading && isEmpty(treeData)) ||
    isSaveLoading ||
    isFetchLatestEventLoading ? (
    <Spin />
  ) : (
    <>
      <StyledPageHeader>
        <StyledPageTitle>Ingredients</StyledPageTitle>
        <StyledPageActions>
          <StyledVerticalSpace>
            <Space>
              <UpdateDataText
                updatedAt={latestEvent?.createdAt}
                updatedBy={latestEvent?.createdBy?.name}
              />

              <HistoryLink
                filterEventTypes
                renderEvent={renderIngredientEvent}
                eventMetadataTypes={eventEntityTypes}
              />
            </Space>
            <FormButtons
              permissions={[PERMISSIONS.METADATA_TREE.SAVE]}
              isDisabled={isMoveActive || isReplaceActive ? true : !isDirty}
              onReset={handleResetTree}
              onSave={handleSaveData}
            />
          </StyledVerticalSpace>
        </StyledPageActions>
      </StyledPageHeader>
      <StyledVerticalSpace>
        {!isEmpty(conflicts) && (
          <IngredientReplaceConflictingErrors errors={conflicts} />
        )}
        <PermissionGuard permissions={[PERMISSIONS.INGREDIENT.LIST_BY_KEYWORD]}>
          <IngredientSearchByKeyword />
        </PermissionGuard>
        <ReduxMetadataTree
          disableLink={!hasPermissionToViewDetails}
          mainTreeExpandedKeys={mainTreeExpandedKeys}
          supportingTreeExpandedKeys={supportingTreeExpandedKeys}
          expandMainTree={expandIngredientMainTreeKeys}
          expandSupportingTree={expandIngredientSupportingTreeKeys}
          metadataType={METADATA_TYPES.INGREDIENTS}
          isDirty={isDirty}
          showSearch
          allowReplaceMany
          treeData={treeData}
          editable={hasFullPermission}
          addNode={handleAdd}
          deleteNode={handleDelete}
          renameNode={handleRename}
          moveNodes={handleMove}
          replaceNode={handleReplace}
          revertReplaceNode={handleRevertReplaceIngredient}
          setDataForRenaming={handleSetDataForRenaming}
          setIsCurationEnabled={handleSetIsCurationEnabled}
          dataForRenaming={dataForRenaming}
          computeNodeHighlight={computeNodeHighlight}
          isMoveActive={isMoveActive}
          setIsMoveActive={setIsMoveActive}
          isReplaceActive={isReplaceActive}
          setIsReplaceActive={setIsReplaceActive}
        />
      </StyledVerticalSpace>
    </>
  )
}

export default IngredientsPage
